import { Box, SxProps, Theme, Typography } from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import { documentationEmpty } from 'assets/icons';
import { FormattedMessage, formatTranslation } from 'intl';
import { DocumentationTranslationKeys } from 'intl/keys';
import { ReactElement } from 'react';
import { useIntl } from 'react-intl';
import { formatVersionNumber } from 'shared/domain/documentation/versionState';
import { Color } from 'shared/utils/colors';
import { useGalleryContext } from '.';
import type { DisplayedFile } from './filesStore';
import FileDisplay from './fileTypesDisplay/fileDisplay';
import { FileDisplayProps } from './fileTypesDisplay/fileDisplay/types';

const wrapperSx: SxProps<Theme> = { position: 'relative', width: '100%', height: '100%' };
const dimmedSx: SxProps<Theme> = { width: '100%', height: '100%', opacity: 0.5 };
const withdrawnNoticeSx: SxProps<Theme> = {
  position: 'absolute',
  bottom: 16,
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  px: 1.75,
  py: 1,
  borderRadius: 1,
  backgroundColor: Color.white,
  color: Color.textPrimary,
  boxShadow: 1,
  whiteSpace: 'nowrap',
};
const withdrawnIconSx: SxProps<Theme> = { color: Color.red };
const emptyStateSx: SxProps<Theme> = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1.5,
  color: Color.white,
};

function noCurrentVersionSelector(file: DisplayedFile | undefined): boolean {
  return Boolean(file && file.currentVersionNumber === null && !file.versionId);
}

function withdrawnVersionSelector(file: DisplayedFile | undefined): number | undefined {
  return file?.withdrawnVersionNumber;
}

/**
 * The gallery's main file, for documentations as in the PT-7117 mockup: an
 * empty state without a current version, a dimmed file under a notice when
 * the shown version is withdrawn.
 */
export function VersionedFileDisplay({ drawing }: FileDisplayProps): ReactElement {
  const intl = useIntl();
  const { useMainDisplayedFile } = useGalleryContext();
  const [noCurrentVersion] = useMainDisplayedFile(noCurrentVersionSelector);
  const [withdrawnVersionNumber] = useMainDisplayedFile(withdrawnVersionSelector);

  if (noCurrentVersion) {
    return (
      <Box sx={emptyStateSx} data-qa='gallery-no-current-version'>
        <img src={documentationEmpty} width={88} height={88} alt='' />
        <Typography variant='subtitle1' fontWeight={600}>
          {formatTranslation(intl, DocumentationTranslationKeys.noCurrentVersion)}
        </Typography>
      </Box>
    );
  }

  if (!withdrawnVersionNumber) {
    return <FileDisplay drawing={drawing} />;
  }

  return (
    <Box sx={wrapperSx}>
      <Box sx={dimmedSx}>
        <FileDisplay drawing={drawing} />
      </Box>
      <Box sx={withdrawnNoticeSx} data-qa='gallery-withdrawn-version-notice'>
        <BlockIcon fontSize='small' sx={withdrawnIconSx} />
        <Typography variant='body2' component='span'>
          <FormattedMessage
            id={DocumentationTranslationKeys.versionWithdrawnNotice}
            values={{ version: <b>{formatVersionNumber(withdrawnVersionNumber)}</b> }}
          />
        </Typography>
      </Box>
    </Box>
  );
}
